import { Button, Stack } from "@mui/material";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";

import { useCreateNotificationHandlerNotificationHandlersPostMutation } from "api";
import { PhoneInput, TextInput, TimeInput } from "components/forms";
import { INotificationHandlerSMS } from "interfaces/notification.interface";

const smsHandlerSchema = z.object({
  description: z.string(),
  configuration: z.object({
    recipients: z.array(
      z.object({
        phone_number: z
          .string()
          .trim()
          .regex(/^(?:\+47|0047)\s?[49]\d{7}$/, {
            message: "Ugyldig mobilnummer",
          }),
        time_restriction: z
          .object({
            start: z.string(),
            end: z.string(),
          })
          .nullable(),
      }),
    ),
  }),
});

type SmsHandlerSchema = z.infer<typeof smsHandlerSchema>;

type TempSmsProps = {
  disabled: boolean;
  zoneId: string;
  onClose: () => void;
  defaultValues: INotificationHandlerSMS | null;
};

export function TempSMS({
  disabled,
  zoneId,
  onClose,
  defaultValues,
}: TempSmsProps) {
  const { t } = useTranslation();

  const [createHandler] =
    useCreateNotificationHandlerNotificationHandlersPostMutation();

  const { control, handleSubmit } = useForm<SmsHandlerSchema>({
    resolver: zodResolver(smsHandlerSchema),
    defaultValues: defaultValues ?? {
      configuration: {
        recipients: [
          {
            phone_number: "+47",
            time_restriction: {
              start: "00:00:00",
              end: "00:00:00",
            },
          },
        ],
      },
    },
  });

  const onCreate = async (data: SmsHandlerSchema) => {
    await createHandler({
      notificationHandlerCreate: {
        type: "sms",
        zone_id: zoneId,
        enabled: true,
        description: data.description,
        configuration: {
          recipients: data.configuration.recipients.map((r) => ({
            time_restriction: r.time_restriction,
            phone_number: r.phone_number,
          })),
        },
      },
    });

    onClose();
  };

  return (
    <Stack
      component={"form"}
      onSubmit={handleSubmit(onCreate)}
      sx={{
        gap: 1,
      }}
    >
      <TextInput
        fieldName={"description"}
        control={control}
        label={t("Description")}
        placeholder={t("notification_sms_handler_description_placeholder")}
        disabled={disabled}
        required
      />
      <PhoneInput
        fieldName={"configuration.recipients.0.phone_number"}
        control={control}
        label={t("phone_number")}
        disabled={disabled}
      />
      <TimeInput
        fieldName={"configuration.recipients.0.time_restriction.start"}
        control={control}
        label={t("active_from")}
        disabled={disabled}
      />
      <TimeInput
        fieldName={"configuration.recipients.0.time_restriction.end"}
        control={control}
        label={t("active_to")}
        disabled={disabled}
      />
      <Stack
        direction={"row"}
        sx={{
          gap: 2,
        }}
      >
        {!defaultValues && <Button type={"submit"}>{t("Create")}</Button>}
        <Button variant={"text"} onClick={onClose}>
          {t("Cancel")}
        </Button>
      </Stack>
    </Stack>
  );
}
